import React, { createContext, useEffect, useReducer } from "react";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { MatxLoading } from "app/components";
import Swal from "sweetalert2";
import { setAuthTokens, clearAuthTokens } from "../authStorage";
import { getApiBaseUrl } from "../apiBase";
import { rotateRefreshToken } from "../refreshTokens";

/** Refresh access this long before JWT exp so API calls rarely hit 401. */
const ACCESS_REFRESH_SKEW_MS = 2 * 60 * 1000;

const initialState = {
  isAuthenticated: false,
  isInitialised: false,
  user: null,
};

const isValidToken = (token) => {
  if (!token) {
    return false;
  }

  const decodedToken = jwtDecode(token);
  const currentTime = Date.now() / 1000;
  return decodedToken.exp > currentTime;
};

const reducer = (state, action) => {
  switch (action.type) {
    case "INIT": {
      const { isAuthenticated, user } = action.payload;
      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user,
      };
    }
    case "FORGOT_PASSWORD": {
      const { user } = action.payload;
      return {
        ...state,
        isAuthenticated: false,
        user: user,
      };
    }
    case "LOGIN": {
      const { user } = action.payload;
      return {
        ...state,
        isAuthenticated: true,
        user,
      };
    }
    case "LOGOUT": {
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      };
    }
    case "REGISTER": {
      const { user } = action.payload;
      return {
        ...state,
        isAuthenticated: true,
        user: user,
      };
    }
    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext({
  ...initialState,
  method: "JWT",
  login: () => Promise.resolve(),
  logout: () => {},
  forgotpassword: () => {},
  register: () => Promise.resolve(),
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const forgotpassword = async (email) => {
    try {
      const res = await axios.post(
        `${getApiBaseUrl()}/users/forgotpassword`,
        { email },
      );
      dispatch({ type: "FORGOT_PASSWORD", payload: res.data });
      Swal.fire({
        title: "Password reset successful! temporary password sent via email",
        icon: "success",
        timer: 2000,
        showCancelButton: false,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        title: "User does not exists",
        icon: "error",
        timer: 2000,
        showCancelButton: false,
        showConfirmButton: false,
      });
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(
        `${getApiBaseUrl()}/login`,
        { email, password },
      );
      const { accessToken, refreshToken, user } = response.data;
      setAuthTokens(accessToken, refreshToken);
      dispatch({ type: "LOGIN", payload: { user } });
    } catch (error) {
      // Re-throw with structured error info from the API response
      const apiError = error.response?.data;
      const enhancedError = new Error(apiError?.msg || "Login failed");
      enhancedError.attemptsRemaining = apiError?.attemptsRemaining ?? null;
      enhancedError.locked = error.response?.status === 403;
      throw enhancedError;
    }
  };

  const register = async (formData) => {
    try {
      const response = await axios.post(
        `${getApiBaseUrl()}/register`,
        formData,
      );
      const { accessToken, refreshToken, user } = response.data;
      setAuthTokens(accessToken, refreshToken);
      dispatch({ type: "REGISTER", payload: { user } });
      Swal.fire({
        title: "Registration Success!",
        icon: "success",
        timer: 2000,
        showCancelButton: false,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.fire({
        title: "Email Already Exist",
        icon: "error",
        timer: 2000,
        showCancelButton: false,
        showConfirmButton: false,
      });
    }
  };

  const logout = async () => {
    const refreshToken = window.localStorage.getItem("refreshToken");
    try {
      if (refreshToken) {
        await axios.post(`${getApiBaseUrl()}/logout`, { refreshToken });
      }
    } catch {
      /* still clear client session */
    }
    clearAuthTokens();
    dispatch({ type: "LOGOUT" });
  };

  useEffect(() => {
    (async () => {
      try {
        const accessToken = window.localStorage.getItem("accessToken");
        const refreshToken = window.localStorage.getItem("refreshToken");
        const api = getApiBaseUrl();

        if (accessToken && isValidToken(accessToken)) {
          setAuthTokens(accessToken);
          const response = await axios.get(`${api}/profile`);
          const user = response.data;
          dispatch({ type: "INIT", payload: { isAuthenticated: true, user } });
        } else if (refreshToken && isValidToken(refreshToken)) {
          await rotateRefreshToken();
          const response = await axios.get(`${api}/profile`);
          const user = response.data;
          dispatch({ type: "INIT", payload: { isAuthenticated: true, user } });
        } else {
          clearAuthTokens();
          dispatch({
            type: "INIT",
            payload: { isAuthenticated: false, user: null },
          });
        }
      } catch (err) {
        console.error(err);
        clearAuthTokens();
        dispatch({
          type: "INIT",
          payload: { isAuthenticated: false, user: null },
        });
      }
    })();
  }, []);

  useEffect(() => {
    if (!state.isAuthenticated) {
      return undefined;
    }

    let cancelled = false;
    let timeoutId;

    const scheduleNextRotation = () => {
      if (cancelled) return;
      const access = localStorage.getItem("accessToken");
      const refresh = localStorage.getItem("refreshToken");
      if (!access || !refresh) return;

      let expMs;
      try {
        expMs = jwtDecode(access).exp * 1000;
      } catch {
        return;
      }

      const msUntil = expMs - Date.now() - ACCESS_REFRESH_SKEW_MS;
      const delay = msUntil <= 0 ? 0 : msUntil;

      clearTimeout(timeoutId);
      timeoutId = setTimeout(async () => {
        if (cancelled) return;
        if (!localStorage.getItem("refreshToken")) return;
        try {
          await rotateRefreshToken();
          if (!cancelled) scheduleNextRotation();
        } catch {
          if (!cancelled) {
            clearAuthTokens();
            dispatch({ type: "LOGOUT" });
          }
        }
      }, delay);
    };

    scheduleNextRotation();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [state.isAuthenticated]);

  if (!state.isInitialised) {
    return <MatxLoading />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "JWT",
        login,
        logout,
        register,
        forgotpassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
